import axios from 'axios';
import { NO_COMPANY_SCOPE_REQUEST_CONFIG } from '../../hooks/dashboard';

export const createCheckoutSession = async () => {
  let res = await axios.post('/billing/checkout-session');
  return res.data;
};

/**
 * ROAR-4944 — Payments & Invoices mutations (AC5, AC6, AC7).
 *
 * AC9: every one of these is company-scoped SERVER-SIDE by the P1.10 scope-resolver middleware. No
 * company identifier is sent — not as an argument, not in the body, and not as a header:
 * `NO_COMPANY_SCOPE_REQUEST_CONFIG` (exported from hooks/dashboard.js by ROAR-4940) deletes the
 * `company_id`/`selected_company_uuid` headers that src/index.js sets at bootstrap. It must DELETE,
 * not null them — axios 0.26 serializes a null header as "null".
 *
 * These are the FIRST calls to send a BODY through that config, so `api/mutations/billing.companyScope.test.js`
 * also asserts the body survives the transform chain.
 *
 * Importing that config pulls in hooks/dashboard.js's ESM-only `query-string` dependency, so any
 * test touching this module needs `jest.mock('query-string', () => ({ stringify: jest.fn() }))`.
 */

/**
 * A Stripe Customer Portal session.
 *
 * Both buttons hit this one endpoint; the `flow` discriminator is what differs — `default` for
 * View Invoices (AC5), `payment_method_update` for Manage Cards (AC6). Siemba never renders an
 * invoice table or a card form of its own (AC10); responsibility ends at the redirect.
 *
 * @param {object} [params]
 * @param {'default'|'payment_method_update'} [params.flow] Which portal surface to land on.
 * @returns {Promise<{url: string}>} The portal url to redirect to.
 */
export const createPortalSession = async ({ flow = 'default' } = {}) => {
  const res = await axios.post(
    '/billing/portal-session',
    { flow },
    NO_COMPANY_SCOPE_REQUEST_CONFIG
  );
  return res.data;
};

/**
 * Requests cancellation at the end of the current billing period (AC7).
 *
 * **A 409 is not a failure.** It means a cancellation is already pending — another admin got there
 * first, or this tab is a refetch behind. The guard is server-side and authoritative, so the error
 * is left intact (`error.response.status`) for `useCancelSubscription` to recognize; collapsing it
 * into a generic Error would turn a recoverable stale view into an error popup (§4 decision 16).
 *
 * @returns {Promise<{cancellation_requested_at: string, renews_at: string}>}
 */
export const cancelSubscription = async () => {
  const res = await axios.post(
    '/billing/subscription/cancel',
    undefined,
    NO_COMPANY_SCOPE_REQUEST_CONFIG
  );
  return res.data;
};

/**
 * Withdraws a pending cancellation (§4 decision 16).
 *
 * Nulls `cancellation_requested_at` server-side. This and the period ending via P1.12's
 * `customer.subscription.deleted` webhook are the only two exits from the pending state.
 *
 * @returns {Promise<{cancellation_requested_at: null, renews_at: string}>}
 */
export const resumeSubscription = async () => {
  const res = await axios.post(
    '/billing/subscription/resume',
    undefined,
    NO_COMPANY_SCOPE_REQUEST_CONFIG
  );
  return res.data;
};

/**
 * ROAR-5007 — ends the caller's Stripe trial now ("Start Early Now" on the credits badge).
 *
 * Stripe is told `trial_end: 'now'`; the plan's first invoice and its credit grant then arrive
 * asynchronously via the `invoice.payment_succeeded` webhook, so the success response says nothing
 * about the new balance yet. A **404** means the company has no live trialing Stripe subscription
 * (already converted in another tab, or an enterprise contract) — the error is left intact so the
 * caller can tell it apart from a genuine failure.
 *
 * @returns {Promise<{subscriptionId: string}>}
 */
export const endTrial = async () => {
  const res = await axios.post(
    '/billing/subscription/end-trial',
    undefined,
    NO_COMPANY_SCOPE_REQUEST_CONFIG
  );
  return res.data;
};
